import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { AppDispatch } from 'config/redux'
import { RootState } from 'constants/interfaces'
import {
  isCommentsCounterRequestPending,
  scheduleCommentsCounterFetch,
} from 'components/TasksV2/hooks/commentsCounterQueue'

import { ContextRow } from './useTaskDashboardData'

const COMMENTS_PREFETCH_MARGIN = '160px 0px'
const COMMENTS_INTERSECTION_THRESHOLD = 0.01
const COMMENTS_FALLBACK_VISIBLE_ROWS = 8

type CommentCounterInfo = {
  totalCommentsCount: number
  unredCommentsCounter: number
  loading: boolean
}

export const useVisibleContextComments = (contexts: ContextRow[], resetKey: string) => {
  const dispatch: AppDispatch = useDispatch()
  const commentsCounter = useSelector((state: RootState) => state.comments.commentsCounter)
  const viewedCommentsIds = useSelector((state: RootState) => state.comments.viewedCommentsIds)

  const [visibleContextIds, setVisibleContextIds] = useState<Set<string>>(new Set())
  const [isFetchingCounters, setIsFetchingCounters] = useState(false)
  const [counterQueueVersion, setCounterQueueVersion] = useState(0)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const observedNodesRef = useRef<Map<string, Element>>(new Map())
  const subscribedOidsRef = useRef<Set<string>>(new Set())
  const unsubscribeCounterRequestsRef = useRef<VoidFunction[]>([])
  const commentsCounterRef = useRef(commentsCounter)
  const fetchRunIdRef = useRef(0)
  const refCallbacksRef = useRef<Map<string, (node: HTMLElement | null) => void>>(new Map())

  commentsCounterRef.current = commentsCounter

  const contextOidsById = useMemo(
    () =>
      contexts.reduce<Record<string, string[]>>((acc, context) => {
        acc[context.contextId] = context.commentOids || []
        return acc
      }, {}),
    [contexts]
  )

  const visibleOids = useMemo(
    () =>
      Array.from(visibleContextIds).flatMap((contextId) => {
        return contextOidsById[contextId] || []
      }),
    [contextOidsById, visibleContextIds]
  )
  const visibleOidsKey = useMemo(() => visibleOids.join('|'), [visibleOids])

  useEffect(() => {
    fetchRunIdRef.current += 1
    subscribedOidsRef.current.clear()
    unsubscribeCounterRequestsRef.current.forEach((unsubscribe) => unsubscribe())
    unsubscribeCounterRequestsRef.current = []
    refCallbacksRef.current.clear()
    setVisibleContextIds(new Set())
    setIsFetchingCounters(false)
    observedNodesRef.current.clear()
  }, [resetKey])

  useEffect(() => {
    return () => {
      unsubscribeCounterRequestsRef.current.forEach((unsubscribe) => unsubscribe())
      unsubscribeCounterRequestsRef.current = []
    }
  }, [])

  // Stable string key — observer only recreates when context IDs actually change, not on array reference change
  const contextIdsKey = contexts.map((c) => c.contextId).join('|')

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setVisibleContextIds(
        new Set(contexts.slice(0, COMMENTS_FALLBACK_VISIBLE_ROWS).map((context) => context.contextId))
      )
      return
    }

    observerRef.current?.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        setVisibleContextIds((prev) => {
          const next = new Set(prev)
          entries.forEach((entry) => {
            const contextId = entry.target.getAttribute('data-context-id')
            if (!contextId) return
            if (entry.isIntersecting) next.add(contextId)
          })
          return next
        })
      },
      { root: null, rootMargin: COMMENTS_PREFETCH_MARGIN, threshold: COMMENTS_INTERSECTION_THRESHOLD }
    )

    observedNodesRef.current.forEach((node) => observerRef.current?.observe(node))
    return () => observerRef.current?.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextIdsKey])

  useEffect(() => {
    const runId = fetchRunIdRef.current
    const oidsToFetch = visibleOidsKey
      ? visibleOidsKey
          .split('|')
          .filter((oid) => !(oid in commentsCounterRef.current) && !subscribedOidsRef.current.has(oid))
      : []
    if (oidsToFetch.length === 0) {
      const hasPendingCounters = visibleOidsKey
        ? visibleOidsKey
            .split('|')
            .some((oid) => !(oid in commentsCounterRef.current) && isCommentsCounterRequestPending(oid))
        : false
      setIsFetchingCounters(hasPendingCounters)
      return
    }

    const fetchCounters = () => {
      if (runId !== fetchRunIdRef.current) return

      setIsFetchingCounters(true)
      const unsubscribers = oidsToFetch.map((oid) => {
        subscribedOidsRef.current.add(oid)
        return scheduleCommentsCounterFetch(oid, dispatch, () => setCounterQueueVersion((version) => version + 1))
      })
      unsubscribeCounterRequestsRef.current.push(...unsubscribers)
    }

    const timer = setTimeout(fetchCounters, 800)
    return () => clearTimeout(timer)
  }, [counterQueueVersion, dispatch, visibleOidsKey])

  const registerContextNode = useCallback((contextId: string) => {
    if (!refCallbacksRef.current.has(contextId)) {
      refCallbacksRef.current.set(contextId, (node: HTMLElement | null) => {
        const previousNode = observedNodesRef.current.get(contextId)
        if (previousNode) observerRef.current?.unobserve(previousNode)

        if (!node) {
          observedNodesRef.current.delete(contextId)
          refCallbacksRef.current.delete(contextId)
          return
        }

        observedNodesRef.current.set(contextId, node)
        observerRef.current?.observe(node)
      })
    }
    return refCallbacksRef.current.get(contextId)!
  }, [])

  const getCounterInfo = useCallback(
    (context: ContextRow): CommentCounterInfo => {
      const oids = context.commentOids || []
      const totalCommentsCount = oids.reduce((total, oid) => total + (commentsCounter[oid] || 0), 0)
      const viewedIds = oids.reduce<string[]>((acc, oid) => {
        if (viewedCommentsIds[oid]) acc.push(...viewedCommentsIds[oid])
        return acc
      }, [])
      const unredCommentsCounter = Math.max(totalCommentsCount - viewedIds.length, 0)
      const isContextVisible = visibleContextIds.has(context.contextId)
      const hasMissingCounters = oids.some((oid) => !(oid in commentsCounter) && isCommentsCounterRequestPending(oid))

      return {
        totalCommentsCount,
        unredCommentsCounter,
        loading: isContextVisible && isFetchingCounters && hasMissingCounters,
      }
    },
    [commentsCounter, isFetchingCounters, viewedCommentsIds, visibleContextIds]
  )

  return { getCounterInfo, registerContextNode }
}
